import { memo, useState, useCallback, useEffect } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import styles from './MobileHeader.module.css'
import SITE from '@/config/site'
import LanguageSwitcher from '@/components/LanguageSwitcher/LanguageSwitcher'
import { HEADER_NAV_ITEMS } from '@/config/navigation'
import { useLocalePath } from '@/hooks/useLocalePath'

const MobileHeader = memo(() => {
  const [isOpen, setIsOpen] = useState(false)
  const { t } = useTranslation()
  const location = useLocation()
  const localePath = useLocalePath()
  const isActive = (path: string) => location.pathname === localePath(path)

  useEffect(() => {
    if (!isOpen) return
    const previousOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.body.style.overflow = previousOverflow
    }
  }, [isOpen])

  const toggleMenu = useCallback(() => {
    setIsOpen(prev => !prev)
  }, [])

  const closeMenu = useCallback(() => {
    setIsOpen(false)
  }, [])

  return (
    <header className={styles.mobileHeader}>
      <div className={styles.container}>
        {/* Logo/Name */}
        <Link to={localePath('/')} className={styles.logo}>
          {SITE.organization.name}
        </Link>

        {/* Hamburger Button */}
        <button
          className={`${styles.hamburger} ${isOpen ? styles.active : ''}`}
          onClick={toggleMenu}
          aria-label="Toggle menu"
        >
          <span></span>
          <span></span>
          <span></span>
        </button>
      </div>

      {/* Mobile Menu */}
      {isOpen && (
        <nav className={styles.menu}>
          {HEADER_NAV_ITEMS.map((item) => (
            <Link
              key={item.path}
              to={localePath(item.path)}
              className={`${styles.menuLink} ${isActive(item.path) ? styles.active : ''}`}
              onClick={closeMenu}
            >
              {t(item.labelKey)}
            </Link>
          ))}

          {/* Language Switcher inside mobile menu */}
          <div className={styles.langSwitcher}>
            <LanguageSwitcher />
          </div>
        </nav>
      )}
    </header>
  )
})

MobileHeader.displayName = 'MobileHeader'

export default MobileHeader
